import React from 'react';
import { createRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import Bowser from 'bowser';
import * as serviceWorker from './serviceWorker';
import { App } from './app';
import { Store } from './store/store';
import { StoreContext } from './store/storeHooks';
import {
  addGoogleTagManager,
  addHubspotTracking,
  addLinkedInTracking,
} from './services/analytics';
import { Config } from './config';
import { TRACE, TRACE_SEPARATOR_FAR_FROM_DECENT } from './core/debug/debugMacros';
import { RUNTIME } from './core/debug';

TRACE_SEPARATOR_FAR_FROM_DECENT(RUNTIME);
TRACE(RUNTIME, `Started CLIPr at ${new Date().toISOString()} for:`, window.location.href);

// A tab opened before a deploy requests chunks the deploy removed; reload once to get the new build.
window.addEventListener('vite:preloadError', event => {
  const key = 'clipr:preloadErrorReloadAt';
  const last = Number(sessionStorage.getItem(key)) || 0;
  if (Date.now() - last < 60_000)
    return; // Already reloaded recently; let the error surface instead of looping.
  sessionStorage.setItem(key, String(Date.now()));
  event.preventDefault();
  window.location.reload();
});

function init() {

  const store = new Store();

  addGoogleTagManager();
  addLinkedInTracking();
  addHubspotTracking();

  if (import.meta.env.DEV)
    (window as unknown as Record<string, unknown>).clipr = store;

  store.init();

  let appElem = (
    <StoreContext.Provider value={store}>
      <HelmetProvider>
        <App />
      </HelmetProvider>
    </StoreContext.Provider>
  );

  if (Config.vendor.react.useStrictMode) {
    appElem = (
      <React.StrictMode>
        {appElem}
      </React.StrictMode>
    );
  }

  const container = document.getElementById('root')!;
  const root = createRoot(container);
  root.render(appElem);
}

function checkBrowser() {
  const browser = Bowser.getParser(window.navigator.userAgent);
  const isValidBrowser = browser.satisfies({
    chrome: '>=75',
    safari: '>=11',
  });

  const skipBrowserCheck = localStorage.getItem('skipBrowserCheck');

  if (!skipBrowserCheck && !isValidBrowser) {
    document.location.href = '/unsupported/unsupportedDesktopBrowser.html';
  }
}

checkBrowser();
init();

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
